import { If, location, onMount, reactive } from 'cdui-js';
import i18n from '../../../../i18n';
import { DiFiSkeleton, DiFiSkeletonParagraph } from '../../../../components/DiFi/DiFiSkeleton';
import { CurrencyFlag } from '../../../../components/CurrencyFlag';
import { downArrowMuted as downArrow } from '../../../../assets/commonIcons';
import { fetchDeWiInfo, fetchStockAccountsMeta, fetchStockAccountsWithdrawalBal, fetchUserBanks } from '../../../../api/funds';
import { showActionSheet } from '../../components/ActionSheet';
import { ActionSheetCheckIcon } from '../../components/ActionSheet/CheckIcon';
import { BtnLoading } from '../../components/BtnLoading';
import { showModal } from '../../components/Modal';
import { bootDepositApp, getBackToAppUrl, getDepositAppQuery, getDepositEntry, type DepositMode } from '../h5-deposit/appBridge';
import { DepositShell } from '../h5-deposit/Shell';
import {
  accountNumberOf,
  activeUserBanks,
  errorText,
  overviewAccountsOf,
  stockAccountLabel,
  unwrapList,
} from '../h5-deposit/shared';
import {
  applyAccount,
  bankIdentity,
  calcWithdrawAllAmount,
  feeTextOf,
  isNumericBalance,
  leaveToRecords,
  leaveWithdraw,
  markWithdrawDone,
  pickWithdrawAccount,
  sanitizeWithdrawAmount,
  shouldExitWithdraw,
  withdrawalBalanceOf,
  withdrawBankLine,
  withdrawRecordsUrl,
  withdrawVisibleAccounts,
  type Currency,
} from './shared';
import { openForgetTradePassword, submitWithdrawal, withdrawErrorMessage } from './submit';
import '../h5-deposit/deposit.css';
import './withdraw.css';

const currencies = (): { key: Currency; label: string }[] => [
  { key: 'HKD', label: i18n.FundH5.CurrencyHkd },
  { key: 'USD', label: i18n.FundH5.CurrencyUsd },
  { key: 'CNY', label: i18n.FundH5.CurrencyCny },
];

const emptyErrors = () => ({ account: '', bank: '', amount: '' });

const SHEET_CLOSE_MS = 320;

const withdrawSkeleton = () => (
  <div class="flex-column gap-12px width-100">
    <DiFiSkeletonParagraph rowWidth="30%" style={{ height: '16px' }} />
    <div class="flex-row gap-8px">
      <DiFiSkeletonParagraph rowWidth="30%" style={{ height: '36px', 'border-radius': '8px', flex: 1 }} />
      <DiFiSkeletonParagraph rowWidth="30%" style={{ height: '36px', 'border-radius': '8px', flex: 1 }} />
      <DiFiSkeletonParagraph rowWidth="30%" style={{ height: '36px', 'border-radius': '8px', flex: 1 }} />
    </div>
    <DiFiSkeletonParagraph rowWidth="30%" style={{ height: '16px', 'margin-top': '8px' }} />
    <DiFiSkeletonParagraph rowWidth="100%" style={{ height: '44px', 'border-radius': '8px' }} />
    <DiFiSkeletonParagraph rowWidth="100%" style={{ height: '44px', 'border-radius': '8px' }} />
    <DiFiSkeletonParagraph rowWidth="30%" style={{ height: '16px', 'margin-top': '8px' }} />
    <DiFiSkeletonParagraph rowWidth="100%" style={{ height: '44px', 'border-radius': '8px' }} />
    <DiFiSkeletonParagraph rowWidth="100%" style={{ height: '14px' }} />
    <DiFiSkeletonParagraph rowWidth="100%" style={{ height: '14px' }} />
  </div>
);

export const WithdrawPage = () => {
  /** 提交成功后再经历史回到本页：直接离开，勿渲染表单（避免空 /account 白屏那条链路） */
  const exiting = shouldExitWithdraw();

  const state = reactive({
    accounts: [] as any[],
    currency: 'HKD' as Currency,
    accountNum: '',
    subUserID: '',
    banks: [] as any[],
    bankKey: '',
    amount: '',
    /** 当前账户+币种可提现；单独拉 WithdrawalBal，不跟账户列表绑死 */
    availableBal: '' as string | number,
    chargeReady: false,
    chargeAmount: '' as string | number,
    chargeCurrency: '',
    password: '',
    passwordError: '',
    submitting: false,
    errors: emptyErrors(),
    accountsLoading: true,
    banksLoading: true,
    chargeLoading: true,
    /** loading：等会话；form：法币出金（证券 / 虚拟同一套） */
    gate: 'loading' as 'loading' | 'form',
    mode: '' as DepositMode,
  });

  let requestTicket = 0;

  const list = () =>
    state.mode === 'virtual' ? currencies().filter((item) => item.key !== 'CNY') : currencies();
  const visibleAccounts = () => withdrawVisibleAccounts(state.accounts, state.currency, state.mode);
  const selectedAccount = () => visibleAccounts().find((row) => accountNumberOf(row) === state.accountNum);
  const selectedBank = () => state.banks.find((row) => bankIdentity(row) === state.bankKey);
  const balanceRaw = () => state.availableBal;
  const balanceText = () => (isNumericBalance(balanceRaw()) ? `${balanceRaw()} ${state.currency}` : '');
  const feeText = () => (state.chargeReady ? feeTextOf(state.chargeAmount, state.chargeCurrency, state.currency) : '');
  const accountText = () => {
    const row = selectedAccount();
    return row ? stockAccountLabel(row) : '';
  };

  onMount(() => {
    if (exiting) {
      leaveWithdraw();
      return;
    }
    void load();
  });

  const selectInitial = (listRows: any[]) => {
    const picked = pickWithdrawAccount(withdrawVisibleAccounts(listRows, state.currency, state.mode));
    const next = applyAccount(picked);
    state.accountNum = next.accountNum;
    state.subUserID = next.subUserID;
  };

  const keepAccountInView = () => {
    const visible = visibleAccounts();
    if (visible.some((row) => accountNumberOf(row) === state.accountNum)) {
      const row = visible.find((item) => accountNumberOf(item) === state.accountNum);
      state.subUserID = applyAccount(row).subUserID;
      return;
    }
    const picked = pickWithdrawAccount(visible);
    const next = applyAccount(picked);
    state.accountNum = next.accountNum;
    state.subUserID = next.subUserID;
  };

  /** 先 prepare，再拉可提现与用户卡。同一次币种/账户变化只认最后一单 */
  const refreshQuote = async (resetBank: boolean) => {
    const ticket = ++requestTicket;
    const currency = state.currency;
    const accountNum = state.accountNum;
    state.banksLoading = true;
    state.chargeLoading = true;
    state.chargeReady = false;
    state.availableBal = '';
    try {
      const prepare = await fetchDeWiInfo({
        type: 'withdrawal',
        currency,
        method: 'ACC',
        accountNum,
      });
      if (ticket !== requestTicket) return;
      const charge = (prepare as any)?.withdrawalCharge || {};
      state.chargeAmount = charge.amount == null ? '' : charge.amount;
      state.chargeCurrency = String(charge.currency || '');
      state.chargeReady = true;
    } catch {
      if (ticket !== requestTicket) return;
      state.chargeReady = false;
    }
    if (ticket !== requestTicket) return;
    state.chargeLoading = false;
    if (accountNum) {
      try {
        const bals = overviewAccountsOf(
          await fetchStockAccountsWithdrawalBal({ accountNum, currency }),
        );
        if (ticket !== requestTicket) return;
        state.availableBal = withdrawalBalanceOf(bals[0], currency);
      } catch {
        if (ticket !== requestTicket) return;
        state.availableBal = '';
      }
    }
    try {
      const banks = activeUserBanks(unwrapList(await fetchUserBanks(currency)));
      if (ticket !== requestTicket) return;
      state.banks = banks;
      const keep = !resetBank && banks.some((row) => bankIdentity(row) === state.bankKey);
      if (!keep) state.bankKey = banks[0] ? bankIdentity(banks[0]) : '';
    } catch {
      if (ticket !== requestTicket) return;
      state.banks = [];
      state.bankKey = '';
    }
    if (ticket === requestTicket) state.banksLoading = false;
  };

  const load = async () => {
    await bootDepositApp();
    state.mode = getDepositEntry().mode;
    if (state.mode === 'virtual' && state.currency === 'CNY') {
      state.currency = 'HKD';
    }
    state.gate = 'form';
    state.accountsLoading = true;
    try {
      state.accounts = overviewAccountsOf(await fetchStockAccountsMeta());
    } catch {
      state.accounts = [];
    }
    state.accountsLoading = false;
    selectInitial(state.accounts);
    await refreshQuote(true);
  };

  const changeCurrency = (currency: Currency) => {
    if (state.currency === currency) return;
    state.currency = currency;
    state.amount = '';
    state.bankKey = '';
    state.errors = emptyErrors();
    keepAccountInView();
    void refreshQuote(true);
  };

  const openAccounts = () => {
    if (state.accountsLoading) return;
    const rows = visibleAccounts();
    if (!rows.length) return;
    const sheet = showActionSheet({
      title: i18n.H5Withdraw.SelectWithdrawAccount,
      content: () => (
        <div>
          {rows.map((item) => {
            const num = accountNumberOf(item);
            const active = num === state.accountNum;
            return (
              <div
                class={`action-sheet-option${active ? ' action-sheet-option-active' : ''}`}
                onclick={() => {
                  sheet.close();
                  if (num === state.accountNum) return;
                  const next = applyAccount(item);
                  state.accountNum = next.accountNum;
                  state.subUserID = next.subUserID;
                  state.errors.account = '';
                  void refreshQuote(false);
                }}
              >
                <div class="action-sheet-option-label">{stockAccountLabel(item)}</div>
                {active ? <ActionSheetCheckIcon /> : null}
              </div>
            );
          })}
        </div>
      ),
    });
  };

  const openBanks = () => {
    if (state.banksLoading) return;
    if (!state.banks.length) {
      showModal({
        title: i18n.FundH5.WarmTip,
        content: () => i18n.FundH5.NoBoundBank,
        confirmText: i18n.FundH5.GotIt,
      });
      return;
    }
    const sheet = showActionSheet({
      title: i18n.H5Withdraw.SelectPayeeBank,
      content: () => (
        <div>
          {state.banks.map((item) => {
            const key = bankIdentity(item);
            const active = key === state.bankKey;
            return (
              <div
                class={`action-sheet-option${active ? ' action-sheet-option-active' : ''}`}
                onclick={() => {
                  state.bankKey = key;
                  state.errors.bank = '';
                  sheet.close();
                }}
              >
                <div class="action-sheet-option-label">{withdrawBankLine(item)}</div>
                {active ? <ActionSheetCheckIcon /> : null}
              </div>
            );
          })}
        </div>
      ),
    });
  };

  const fillAll = () => {
    if (!isNumericBalance(balanceRaw())) return;
    state.amount = calcWithdrawAllAmount(
      balanceRaw(),
      state.chargeAmount,
      state.chargeCurrency,
      state.currency
    );
    state.errors.amount = '';
  };

  const validate = () => {
    const errors = emptyErrors();
    if (!state.accountNum) errors.account = i18n.FundH5.PleaseSelect;
    if (!selectedBank()) errors.bank = i18n.H5Withdraw.ErrSelectPayeeBank;
    const amount = String(state.amount || '').trim();
    if (!amount) errors.amount = i18n.H5Withdraw.ErrEnterWithdrawAmount;
    else if (!/^\d+(\.\d{1,3})?$/.test(amount) || Number(amount) <= 0) errors.amount = i18n.H5Withdraw.ErrValidAmount;
    else if (isNumericBalance(balanceRaw()) && Number(amount) > Number(balanceRaw())) {
      errors.amount = i18n.H5Withdraw.ErrExceedBalance;
    }
    state.errors = errors;
    return !errors.account && !errors.bank && !errors.amount;
  };

  const showSuccess = () => {
    const accountNum = state.accountNum;
    showModal({
      title: i18n.FundH5.SubmitSuccess,
      disableClickMask: true,
      content: () => i18n.H5Withdraw.SubmitSuccessDesc,
      cancelText: i18n.FundH5.ViewRecords,
      confirmText: i18n.FundH5.GotIt,
      onCancel: () => {
        const url = withdrawRecordsUrl(accountNum, { isBackToApp: true });
        console.log('[h5-withdraw] 查看记录 点击', {
          from: 'withdraw',
          accountNum,
          isBackToApp: true,
          targetUrl: url,
          backToAppUrl: getBackToAppUrl(),
          entryQuery: getDepositAppQuery(),
        });
        leaveToRecords(accountNum);
      },
      onConfirm: () => {
        markWithdrawDone();
        leaveWithdraw();
      },
    });
  };

  const openPassword = () => {
    state.password = '';
    state.passwordError = '';
    state.submitting = false;
    let lockSheet = false;
    let forgotAfterClose = false;
    const sheet = showActionSheet({
      title: i18n.H5Withdraw.SecurityVerify,
      contentClass: 'deposit-confirm-sheet',
      canClose: () => !lockSheet,
      onClose: () => {
        const openForgot = forgotAfterClose;
        forgotAfterClose = false;
        lockSheet = false;
        state.password = '';
        state.passwordError = '';
        state.submitting = false;
        if (openForgot) openForgetTradePassword();
      },
      content: () => (
        <div>
          <div class="f-input-2 flex align-center">
            <input
              class="flex-auto"
              type="password"
              autocomplete="current-password"
              placeholder={i18n.H5Withdraw.PhTradePassword}
              value={state.password}
              disabled={state.submitting}
              oninput={(event) => {
                state.password = event.currentTarget.value;
                state.passwordError = '';
              }}
              style={{ height: '100%', background: 'transparent', padding: 0 }}
            />
          </div>
          <div class="flex justify-space-between align-center gap-12px margin-t-XXS">
            <div class="withdraw-password-error color-error font-s-XS">{state.passwordError}</div>
            <button
              type="button"
              class="withdraw-forget"
              disabled={state.submitting}
              onclick={() => {
                if (state.submitting || lockSheet) return;
                forgotAfterClose = true;
                sheet.close();
              }}
            >
              {i18n.H5Withdraw.ForgotPassword}
            </button>
          </div>
          <div class="deposit-confirm-actions">
            <button
              type="button"
              class="primary-btn btn-rounded withdraw-password-submit"
              disabled={state.submitting}
              onclick={() => {
                if (lockSheet || state.submitting) return;
                void confirmPassword(
                  () => sheet.close(),
                  (locked) => {
                    lockSheet = locked;
                  },
                );
              }}
            >
              {state.submitting ? <BtnLoading /> : i18n.FundH5.Confirm}
            </button>
          </div>
        </div>
      ),
    });
  };

  const confirmPassword = async (close: () => void, setLocked: (locked: boolean) => void) => {
    if (state.submitting) return;
    const password = String(state.password || '').trim();
    if (!password) {
      state.passwordError = i18n.H5Withdraw.PhTradePassword;
      return;
    }
    const bank = selectedBank();
    if (!bank) {
      state.passwordError = i18n.H5Withdraw.ErrSelectPayeeBank;
      return;
    }
    setLocked(true);
    state.submitting = true;
    state.passwordError = '';
    try {
      await submitWithdrawal({
        accountNum: state.accountNum,
        currency: state.currency,
        payeeBankCode: String(bank.bankCode || ''),
        payeeBankAccountNum: String(bank.bankAccountNum || ''),
        subUserID: state.subUserID,
        amount: Number(state.amount),
        password,
      });
      setLocked(false);
      close();
      setTimeout(showSuccess, 450);
    } catch (error) {
      setLocked(false);
      state.submitting = false;
      state.passwordError = withdrawErrorMessage(error) || errorText(error) || i18n.H5Withdraw.PasswordVerifyFail;
    }
  };

  const openConfirm = () => {
    if (!validate()) return;
    const bank = selectedBank();
    let handed = false;
    const sheet = showActionSheet({
      title: i18n.H5Withdraw.ConfirmWithdrawTitle,
      contentClass: 'deposit-confirm-sheet',
      content: () => (
        <div>
          <div class="flex-column gap-12px font-s-S">
            <div class="flex justify-space-between gap-12px">
              <span class="color">{i18n.H5Withdraw.LabelWithdrawAccount}</span>
              <span class="color-D">{accountText()}</span>
            </div>
            <div class="flex justify-space-between gap-12px">
              <span class="color">{i18n.H5Withdraw.LabelPayeeBank}</span>
              <span class="color-D">{bank ? withdrawBankLine(bank) : ''}</span>
            </div>
            <div class="flex justify-space-between gap-12px">
              <span class="color">{i18n.H5Withdraw.LabelWithdrawAmount}</span>
              <span class="color-D">{`${state.amount} ${state.currency}`}</span>
            </div>
            <div class="flex justify-space-between gap-12px">
              <span class="color">{i18n.FundH5.Fee}</span>
              <span class="color-D">{feeText()}</span>
            </div>
          </div>
          <div class="deposit-confirm-actions">
            <button type="button" class="modal-panel-btn-cancel btn-rounded" onclick={() => sheet.close()}>
              {i18n.FundH5.Cancel}
            </button>
            <button
              type="button"
              class="primary-btn btn-rounded"
              onclick={() => {
                if (handed) return;
                handed = true;
                sheet.close();
                setTimeout(openPassword, SHEET_CLOSE_MS);
              }}
            >
              {i18n.FundH5.Confirm}
            </button>
          </div>
        </div>
      ),
    });
  };

  if (exiting) return null;

  return (
    <DepositShell
      title={i18n.H5Withdraw.Title}
      recordLabel={i18n.H5Withdraw.Records}
      scrollClass="withdraw-scroll"
      onRecord={() => location.routeTo(withdrawRecordsUrl(state.accountNum))}
      footer={
        state.gate === 'form' ? (
          <div class="footer withdraw-footer">
            <button type="button" class="btn-primary" disabled={state.submitting} onclick={openConfirm}>
              {state.submitting ? <BtnLoading /> : i18n.FundH5.Submit}
            </button>
          </div>
        ) : null
      }
    >
      <If when={state.gate === 'loading'}>
        <DiFiSkeleton loading template={withdrawSkeleton()} />
      </If>
      <If when={state.gate === 'form'}>
        <div>
      <div class="withdraw-block">
        <div class="section-title">{i18n.H5Withdraw.SelectCurrency}</div>
        <div class="currency-row">
          {list().map((item) => (
            <button
              type="button"
              class={`currency-btn${state.currency === item.key ? ' is-on' : ''}`}
              onclick={() => changeCurrency(item.key)}
            >
              <CurrencyFlag code={item.key} />
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div class="withdraw-block">
        <div class="section-title">{i18n.H5Withdraw.FillInfo}</div>
        <div class="withdraw-fields">
          <div class="field">
            <div class="field-label">
              {i18n.H5Withdraw.LabelWithdrawAccount}<span class="star">*</span>
            </div>
            <div
              class={`picker${accountText() ? '' : ' is-empty'}${state.errors.account ? ' is-error' : ''}`}
              onclick={openAccounts}
            >
              <span class="withdraw-picker-text">{accountText() || i18n.FundH5.PleaseSelect}</span>
              {state.accountsLoading ? <span class="withdraw-spin" /> : <img class="withdraw-arrow" src={downArrow} alt="" />}
            </div>
            {state.errors.account ? <div class="err">{state.errors.account}</div> : null}
          </div>

          <div class="field">
            <div class="field-label">
              {i18n.H5Withdraw.LabelPayeeBank}<span class="star">*</span>
            </div>
            <div
              class={`picker${selectedBank() ? '' : ' is-empty'}${state.errors.bank ? ' is-error' : ''}`}
              onclick={openBanks}
            >
              <span class="withdraw-picker-text">{selectedBank() ? withdrawBankLine(selectedBank()) : i18n.FundH5.PleaseSelect}</span>
              {state.banksLoading ? <span class="withdraw-spin" /> : <img class="withdraw-arrow" src={downArrow} alt="" />}
            </div>
            {state.errors.bank ? <div class="err">{state.errors.bank}</div> : null}
          </div>
        </div>
      </div>

      <div class="withdraw-block">
        <div class="section-title">{i18n.H5Withdraw.SectionAmount}</div>
        <div class="withdraw-fields">
          <div class="field">
            <div class="field-label">
              {i18n.H5Withdraw.LabelWithdrawAmount}<span class="star">*</span>
            </div>
            <div class={`input${state.errors.amount ? ' is-error' : ''}`}>
              <input
                class="amount-field"
                inputmode="decimal"
                placeholder={i18n.H5Withdraw.PhWithdrawAmount}
                value={state.amount}
                oninput={(event) => {
                  const next = sanitizeWithdrawAmount(event.currentTarget.value);
                  state.amount = next;
                  event.currentTarget.value = next;
                  state.errors.amount = '';
                }}
              />
              <button type="button" class="withdraw-all" onclick={fillAll}>
                {i18n.H5Withdraw.WithdrawAll}
              </button>
            </div>
            {state.errors.amount ? <div class="err">{state.errors.amount}</div> : null}
          </div>

          <div class="info-row">
            <span class="info-label">{i18n.H5Withdraw.AvailableAmount}</span>
            <span class="info-value">
              {state.accountsLoading ? <span class="withdraw-spin" /> : balanceText()}
            </span>
          </div>
          <div class="info-row">
            <span class="info-label">{i18n.FundH5.Fee}</span>
            <span class="info-value">
              {state.chargeLoading ? <span class="withdraw-spin" /> : feeText()}
            </span>
          </div>
          <div class="info-row">
            <span class="info-label">{i18n.H5Withdraw.ArrivalTime}</span>
            <span class="info-value">{i18n.H5Withdraw.ArrivalTimeValue}</span>
          </div>
        </div>
      </div>
        </div>
      </If>
    </DepositShell>
  );
};
